import Image, { StaticImageData } from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { FaFacebookF, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { A } from '../../general/link';
import { useWindowSize } from '../../hooks/useWindowSize';

import { environment } from 'environment';
import candidSealBadge from '../../../assets/candid-seal-platinum-2026.png';
import corpPartners from '../../../assets/home/corp-partners-min.jpg';
import donate from '../../../assets/home/donate-min.jpg';
import volunteer from '../../../assets/home/volunteer-min.jpg';

import { LogoMain } from '../../img/logo';
import { BlueArch, CharityNavigatorBadge, InstagramIcon } from '../../img/svgs';
import { useNavContext } from './context';

const aboutUs = [
  { title: 'who we are', href: '/who-we-are' },
  { title: 'where donations go', href: '/where-donations-go' },
  { title: 'FAQs', href: '/faqs' },
  { title: 'contact us', href: '/contact' },
];

const getInvolved = [
  { title: 'how it works', href: '/how-to-help' },
  { title: 'how to help', href: '/how-to-help' },
  { title: 'news & events', href: '/news' },
  { title: 'donate', href: '/donate' },
];

const partnersDonors = [
  { title: 'corporate partnership', href: '/partners' },
  { title: 'bulk furniture partners', href: '/furniture-partners' },
  { title: 'community donors', href: '/donors' },
  { title: 'referring agencies', href: '/agencies' },
];

export function Footer() {
  const navContext = useNavContext();
  const [archHeight, setArchHeight] = useState(0);
  const window = useWindowSize();

  useEffect(() => {
    const width = window.width;
    setArchHeight(Math.floor(width * (55 / 1600)) - 1);
  }, [window]);

  if (navContext.footer === false) return null;

  return (
    <>
      {navContext.defaultFooter ? (
        <section className="relative bg-blue-500 pb-16 text-white">
          <div className={`absolute w-full`} style={{ top: `-${archHeight}px` }}>
            <BlueArch />
          </div>
          <div>
            <div className="flex flex-col items-center py-4">
              <h2 className="text-center font-operetta text-5xl">
                get involved, <br />
                get connected, change lives.
              </h2>
            </div>
            <div className="mx-auto grid max-w-5xl gap-4 px-8 pt-4 lg:grid-cols-3">
              <CTA link="/how-to-help?tab=volunteer" image={volunteer}>
                <h3 className="text-4xl font-medium">volunteer</h3>
                <p className="text-lg leading-6">
                  Join our mission &amp; volunteer your time towards giving back to kids &amp; families in need
                </p>
                <p className="text-2xl font-medium">get involved »</p>
              </CTA>
              <CTA link="/how-to-help?tab=furniture" image={donate}>
                <h3 className="text-4xl font-medium">donate</h3>
                <p className="text-lg leading-6">
                  Whether it&#39;s new/lightly used furniture, or monetary means, we welcome your donations
                </p>
                <p className="text-2xl font-medium">donate now »</p>
              </CTA>
              <CTA link="/partners" image={corpPartners}>
                <h3 className="text-4xl font-medium">corporate partners</h3>
                <p className="text-lg leading-6">
                  Get your Business or Organization involved in making an impact today!
                </p>
                <p className="text-2xl font-medium">become a partner »</p>
              </CTA>
            </div>
          </div>
        </section>
      ) : null}
      <footer className="relative text-blue-500">
        <FooterDivider />
        <section className="my-16 flex justify-center">
          <A href="/">
            <LogoMain className="w-64" defer />
          </A>
        </section>
        <section className="relative mx-auto grid max-w-5xl gap-4 px-4 md:grid-cols-4">
          <LinkBloc title="about us" links={aboutUs} />
          <LinkBloc title="get involved" links={getInvolved} />
          <LinkBloc title="partners &amp; donors" links={partnersDonors} />
          <div className="flex flex-col items-start font-avenir text-lg font-medium">
            <p className="text-2xl font-semibold">find us</p>
            <p className="text-xs font-bold">mailing</p>
            <A external href="https://goo.gl/maps/xvJugxPQS7P6ToQj7" className="leading-6 hover:underline">
              1800 Camden Rd. Suite 107-17
              <br />
              Charlotte, NC 28203
            </A>
            <p className="text-xs font-bold">warehouse</p>
            <A external href="https://maps.app.goo.gl/LqDLU5MtH3mp8TkE8" className="leading-6 hover:underline">
              2701-A Hutchison McDonald Rd.
              <br />
              Charlotte, NC 28269
            </A>
          </div>
        </section>
        <section className="mx-4 mb-8 mt-8 flex max-w-5xl flex-wrap justify-center border-t-2 border-green-500 pt-8 md:justify-between lg:mx-auto">
          <A external href="tel:+19804221192" className="font-avenir text-2xl font-bold hover:underline">
            (980) 422-1192
          </A>
          <div className="my-4 flex w-full justify-center gap-4 md:my-auto md:w-auto">
            <A external href="https://www.instagram.com/bedsforkids/">
              <InstagramIcon className="h-7 w-7" />
            </A>
            <A external title="LinkedIn" href="https://www.linkedin.com/company/beds-for-kids/">
              <FaLinkedin className="h-7 w-7" />
            </A>
            <A external title="Facebook" href="https://www.facebook.com/bedsforkids">
              <FaFacebookF className="h-7 w-7" />
            </A>
            <A external title="YouTube" href="https://www.youtube.com/@bedsforkids2134">
              <FaYoutube className="h-7 w-7" />
            </A>
          </div>
          <A external href="mailto:info@bedsforkids.org" className="font-avenir text-2xl font-bold hover:underline">
            info@bedsforkids.org
          </A>
        </section>
        <section className="mx-auto my-2 flex max-w-5xl justify-center gap-8 align-middle">
          <CharityNavigatorBadge width={100} height={50} />
          <Image src={candidSealBadge} alt="Platinum Candid Seal" width={100} height={50} />
        </section>
        <section className="mx-auto my-2 max-w-5xl pt-6">
          <p className="text-center text-sm font-semibold xs:text-base">
            &copy; {new Date().getFullYear()} &nbsp;|&nbsp;{' '}
            <A href="/credits" className="hover:underline">
              {environment.version} - Beds for Kids 501(c)3 Non-Profit
            </A>
          </p>
        </section>
      </footer>
    </>
  );
}

export function CTA({ children, link, image }: { children: React.ReactNode; link: string; image: StaticImageData }) {
  return (
    <A href={link}>
      <div className="relative aspect-1 overflow-hidden rounded-xl border-2 border-white">
        <Image
          src={image}
          alt="volunteering"
          sizes="50vw, (min-width: 768px) 33vw"
          className="absolute bottom-0 left-0 right-0 top-0 overflow-hidden object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[#000] opacity-[50%]" />
        <div className="relative flex h-full flex-col justify-center gap-2 p-4 font-avenir">{children}</div>
      </div>
    </A>
  );
}

function LinkBloc({ title, links }: { title: string; links: { title: string; href: string }[] }) {
  return (
    <div className="flex flex-col items-start gap-[.2rem] font-avenir text-lg font-medium">
      <p className="text-2xl font-semibold">{title}</p>
      {links.map(({ title, href }) => (
        <A key={title} href={href} className="w-2/3 hover:underline sm:w-full">
          {title}
        </A>
      ))}
    </div>
  );
}

export function FooterDivider() {
  const ref = useRef<SVGSVGElement>(null);
  const [offset, setOffset] = useState(0);
  const [bgHeight, setBgHeight] = useState(0);
  const window = useWindowSize();

  useEffect(() => {
    if (!ref.current) return;
    const divider = ref.current;
    const dividerHeight = divider.getBoundingClientRect().height;
    const dividerWidth = divider.getBoundingClientRect().width;
    setOffset(Math.floor(dividerHeight - dividerWidth * 0.15));
    setBgHeight(dividerHeight);
  }, [ref, window]);

  return (
    <>
      <div className="pointer-events-none absolute left-0 right-0 z-0" style={{ top: `-${offset - 1}px` }}>
        <svg
          id="footer-divider"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 1600 600"
          ref={ref}>
          <defs>
            <style>
              {
                '\n      .footer-divider-1 {\n        fill: url(#linear-gradient);\n      }\n\n      .footer-divider-2 {\n        fill: url(#linear-gradient-2);\n      }\n\n      .footer-divider-3 {\n        fill: #fff;\n        fill-rule: evenodd;\n      }\n    '
              }
            </style>
            <linearGradient
              id="linear-gradient"
              x1={1505}
              y1={600}
              x2={1505}
              y2={395.34}
              gradientTransform="matrix(1, 0, 0, 1, 0, 0)"
              gradientUnits="userSpaceOnUse">
              <stop offset={0} stopColor="#0f004e" stopOpacity={0} />
              <stop offset={0.21} stopColor="#0f004e" stopOpacity={0.05} />
              <stop offset={0.43} stopColor="#0f004e" stopOpacity={0.21} />
              <stop offset={0.65} stopColor="#0f004e" stopOpacity={0.48} />
              <stop offset={0.87} stopColor="#0f004e" stopOpacity={0.85} />
              <stop offset={0.95} stopColor="#0f004e" />
            </linearGradient>
            <linearGradient id="linear-gradient-2" x1={95} x2={95} y2={395.34} xlinkHref="#linear-gradient" />
          </defs>
          <path
            className="footer-divider-3"
            d="M1600,75.94c0-27.57-20.15-74.94-45-74.94s-45,47.37-45,74.94V400.33h-10s-326.67-49.91-700-49.91S100,400.34,100,400.34h-10v-99.78c0-27.57-20.15-74.94-45-74.94h0c-24.85,0-45,47.37-45,74.94v99.77H0v199.67H1600v-199.66h0V75.94Z"
          />
          <path
            className="footer-divider-1"
            d="M1504.82,395.34h.35c2.66,0,4.82,2.16,4.82,4.82v199.84h-10v-199.84c0-2.66,2.16-4.82,4.82-4.82Z"
          />
          <path
            className="footer-divider-2"
            d="M94.82,395.34h.35c2.66,0,4.82,2.16,4.82,4.82v199.84h-10v-199.84c0-2.66,2.16-4.82,4.82-4.82Z"
          />
        </svg>
      </div>
      <div
        className="absolute left-0 right-0 -z-[1] bg-blue-500"
        style={{ top: `-${offset}px`, height: bgHeight }}></div>
    </>
  );
}
